import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import pqrsService from '@/services/pqrs.service';

const PqrMetricsModal = ({ isOpen, onClose, filters }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadMetrics();
    }
  }, [isOpen, filters]);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const response = await pqrsService.getMetrics(filters);
      if (response.success) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error('Error al cargar métricas:', error);
      toast.error('Error al cargar las métricas');
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'Abierta': 'bg-red-100 text-red-800',
      'En proceso': 'bg-blue-100 text-blue-800',
      'En espera': 'bg-yellow-100 text-yellow-800',
      'Cerrada': 'bg-green-100 text-green-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      'Alta': 'bg-red-100 text-red-800',
      'Media': 'bg-yellow-100 text-yellow-800',
      'Baja': 'bg-green-100 text-green-800'
    };
    return colors[prioridad] || 'bg-gray-100 text-gray-800';
  };

  const getTipoColor = (tipo) => {
    const colors = {
      'Petición': 'bg-blue-100 text-blue-800',
      'Queja': 'bg-yellow-100 text-yellow-800',
      'Reclamo': 'bg-red-100 text-red-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  const getCategoriaColor = (categoria) => {
    const colors = {
      'Logística': 'bg-purple-100 text-purple-800',
      'Calidad del lavado': 'bg-green-100 text-green-800',
      'Entregas': 'bg-blue-100 text-blue-800',
      'Faltantes': 'bg-orange-100 text-orange-800',
      'Reprocesos': 'bg-pink-100 text-pink-800',
      'Insumos': 'bg-teal-100 text-teal-800',
      'Otro': 'bg-gray-100 text-gray-800'
    };
    return colors[categoria] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Métricas de PQRs
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : metrics ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    Total PQRs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-blue-500" />
                    <span className="text-2xl font-bold">{metrics.totalPqrs}</span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    Abiertas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <span className="text-2xl font-bold">
                      {metrics.pqrsPorEstado?.Abierta || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    En Proceso
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-500" />
                    <span className="text-2xl font-bold">
                      {(metrics.pqrsPorEstado?.['En proceso'] || 0) + 
                       (metrics.pqrsPorEstado?.['En espera'] || 0)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500">
                    Cerradas
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <span className="text-2xl font-bold">
                      {metrics.pqrsPorEstado?.Cerrada || 0}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Tiempo Promedio de Respuesta</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-3xl font-bold">
                      {metrics.tiempoPromedioRespuestaDias?.toFixed(1) || 0}
                    </p>
                    <p className="text-sm text-gray-500">días promedio</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PQRs por Estado</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metrics.pqrsPorEstado || {}).map(([estado, count]) => (
                      <div key={estado} className="flex items-center justify-between">
                        <Badge className={getEstadoColor(estado)}>{estado}</Badge>
                        <span className="text-lg font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PQRs por Tipo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metrics.pqrsPorTipo || {}).map(([tipo, count]) => (
                      <div key={tipo} className="flex items-center justify-between">
                        <Badge className={getTipoColor(tipo)}>{tipo}</Badge>
                        <span className="text-lg font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PQRs por Prioridad</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metrics.pqrsPorPrioridad || {}).map(([prioridad, count]) => (
                      <div key={prioridad} className="flex items-center justify-between">
                        <Badge className={getPrioridadColor(prioridad)}>{prioridad}</Badge>
                        <span className="text-lg font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">PQRs por Categoría</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(metrics.pqrsPorCategoria || {}).map(([categoria, count]) => (
                      <div key={categoria} className="flex items-center justify-between">
                        <Badge className={getCategoriaColor(categoria)}>{categoria}</Badge>
                        <span className="text-lg font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">
            No hay datos disponibles
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PqrMetricsModal;
